// Canal « Instantané » (onScreen) — NOTIF-001.
// Poll périodique de la file de toasts EN ATTENTE, drainée côté serveur (livraison
// idempotente : chaque toast n'est livré qu'une fois, même multi-onglets). Monté
// UNE seule fois (NotificationCenter). Le poll est suspendu quand l'onglet est
// masqué (défaut TanStack `refetchIntervalInBackground: false`) → jamais de toast
// « consommé » sans être vu par l'utilisateur.

import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { apiFetch } from '@/lib/api'
import { queryKeys } from '@/lib/queryKeys'

import { entityHeaders, NOTIF_BASE, readError } from './useNotifications.shared'
import type { OnScreenDelivery, OnScreenToast } from './useNotifications.types'

const POLL_MS = 20_000
// Au-delà de ce seuil, un seul toast récapitulatif (anti-inondation d'écran).
const INDIVIDUAL_MAX = 3

export function useOnScreenToasts(entityId: string | null) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const query = useQuery<OnScreenDelivery>({
        queryKey: queryKeys.notifications.onScreen(entityId ?? ''),
        enabled: Boolean(entityId),
        staleTime: 0,
        gcTime: 0,
        refetchInterval: POLL_MS,
        // Ne draine QUE onglet visible : l'intervalle est suspendu en arrière-plan
        // (défaut refetchIntervalInBackground:false) et le focus rend l'onglet visible.
        refetchOnWindowFocus: true,
        // PAS de drainage sur reconnexion réseau (peut survenir onglet masqué →
        // toasts consommés jamais vus) ; PAS de retry (un POST rejoué re-claim et
        // renvoie vide → toast perdu). Le POST CONSOMME la file : livraison at-most-once.
        refetchOnReconnect: false,
        retry: false,
        queryFn: async () => {
            // POST : la lecture CONSOMME la file (marque les toasts livrés en base).
            const resp = await apiFetch(`${NOTIF_BASE}/onscreen/deliver`, {
                method: 'POST',
                headers: entityHeaders(entityId as string),
            })
            if (!resp.ok) throw await readError(resp)
            return resp.json()
        },
    })

    const data = query.data
    // Un lot de données n'est « toasté » qu'une fois (garde contre les re-renders
    // React / StrictMode : on compare la RÉFÉRENCE du tableau, neuve à chaque fetch).
    const lastHandled = useRef<OnScreenToast[] | null>(null)

    useEffect(() => {
        if (!data || data.items.length === 0) return
        if (lastHandled.current === data.items) return
        lastHandled.current = data.items

        const show = (n: OnScreenToast) => {
            const opts = {
                description: n.message ?? undefined,
                action: {
                    label: t('notifications.toast.view'),
                    onClick: () => navigate(`/notifications?open=${n.id}`),
                },
            }
            // Canal réservé à l'urgent : on distingue visuellement la priorité URGENT.
            if (n.priority === 'URGENT') toast.warning(n.title, opts)
            else toast(n.title, opts)
        }

        if (data.items.length <= INDIVIDUAL_MAX) {
            data.items.forEach(show)
        } else {
            toast.warning(t('notifications.toast.summary', { count: data.items.length }), {
                action: {
                    label: t('notifications.toast.view'),
                    onClick: () => navigate('/notifications'),
                },
            })
        }

        // Synchronise le badge de la cloche sans attendre son poll (60 s).
        queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(entityId ?? ''),
        })
    }, [data, entityId, navigate, queryClient, t])
}
